import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';

const SearchFilterSection = ({ filters, onFilterChange, totalResults }) => {
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const categories = [
    { id: 'all', label: 'Alle', icon: 'Grid' },
    { id: 'solar', label: 'Zonnepanelen', icon: 'Sun' },
    { id: 'heatpump', label: 'Warmtepompen', icon: 'Flame' },
    { id: 'insulation', label: 'Isolatie', icon: 'Home' },
    { id: 'battery', label: 'Batterijen', icon: 'Battery' },
    { id: 'ventilation', label: 'Ventilatie', icon: 'Wind' }
  ];

  const ratingOptions = [
    { value: 0, label: 'Alle beoordelingen' },
    { value: 3, label: '3+ sterren' },
    { value: 4, label: '4+ sterren' },
    { value: 4.5, label: '4.5+ sterren' }
  ];

  const handleSearchChange = (e) => {
    onFilterChange({ ...filters, searchQuery: e?.target?.value });
  };

  const handleLocationChange = (e) => {
    onFilterChange({ ...filters, location: e?.target?.value });
  };

  const handleCategoryChange = (categoryId) => {
    onFilterChange({ ...filters, category: categoryId });
  };

  const handleCertifiedToggle = () => {
    onFilterChange({ ...filters, certifiedOnly: !filters?.certifiedOnly });
  };

  const handleRatingChange = (e) => {
    onFilterChange({ ...filters, minRating: parseFloat(e?.target?.value) });
  };

  const handleResetFilters = () => {
    onFilterChange({
      searchQuery: '',
      category: 'all',
      location: '',
      certifiedOnly: false,
      minRating: 0
    });
  };

  return (
    <section className="bg-card border-b border-border sticky top-0 z-40 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Search Bar */}
        <div className="flex flex-col lg:flex-row gap-4 mb-6">
          <div className="flex-1">
            <div className="relative">
              <Icon 
                name="Search" 
                size={20} 
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <Input
                type="text"
                placeholder="Zoek op naam of specialisatie..."
                value={filters?.searchQuery}
                onChange={handleSearchChange}
                className="pl-10"
              />
            </div>
          </div>
          <div className="w-full lg:w-64">
            <div className="relative">
              <Icon 
                name="MapPin" 
                size={20} 
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <Input
                type="text"
                placeholder="Locatie of postcode"
                value={filters?.location}
                onChange={handleLocationChange}
                className="pl-10"
              />
            </div>
          </div>
          <Button
            variant="outline"
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            iconName={showAdvancedFilters ? 'ChevronUp' : 'ChevronDown'}
            iconPosition="right"
          >
            Filters
          </Button>
        </div>

        {/* Category Tabs */}
        <div className="flex overflow-x-auto gap-2 pb-2 scrollbar-hide mb-4">
          {categories?.map((category) => (
            <motion.button
              key={category?.id}
              onClick={() => handleCategoryChange(category?.id)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                filters?.category === category?.id
                  ? 'bg-primary text-primary-foreground shadow-elevation-1'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              <Icon name={category?.icon} size={16} />
              <span>{category?.label}</span>
            </motion.button>
          ))}
        </div>

        {/* Advanced Filters */}
        {showAdvancedFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="border-t border-border pt-4 mt-4"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Certified Only */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="certified"
                  checked={filters?.certifiedOnly}
                  onChange={handleCertifiedToggle}
                />
                <label htmlFor="certified" className="text-sm text-foreground cursor-pointer">
                  Alleen gecertificeerde aanbieders
                </label>
              </div>

              {/* Rating Filter */}
              <div>
                <Select
                  value={filters?.minRating}
                  onChange={handleRatingChange}
                >
                  {ratingOptions?.map((option) => (
                    <option key={option?.value} value={option?.value}>
                      {option?.label}
                    </option>
                  ))}
                </Select>
              </div>

              {/* Reset Button */}
              <div className="flex items-center">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleResetFilters}
                  iconName="X"
                  iconPosition="left"
                >
                  Reset filters
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Results Count */}
        <div className="flex items-center justify-between mt-4 text-sm text-muted-foreground">
          <span>
            {totalResults} {totalResults === 1 ? 'aanbieder' : 'aanbieders'} gevonden
          </span>
        </div>
      </div>
    </section>
  );
};

export default SearchFilterSection;